import { z } from 'zod';

import {
    positionAttributeSchema,
    positionDetailSchema,
    positionPersonSchema,
    positionStatusSchema,
    positionStructuredSchema,
    positionStructuredTaskSchema,
} from './position-management';
import { taskManagementTaskDetailSchema, workTaskAutomationRecommendationSchema } from './task-management';

export const alignmentStepSchema = z.enum(['POSITION', 'TASKS', 'AUTOMATIONS']);

export type AlignmentStep = z.infer<typeof alignmentStepSchema>;

export const ALIGNMENT_STEPS: readonly AlignmentStep[] = alignmentStepSchema.options;

export const managerDecisionSchema = z.enum(['PENDING', 'CONFIRMED', 'CHANGES']);

export type ManagerDecision = z.infer<typeof managerDecisionSchema>;

export const stepStatusSchema = z.enum(['YOURS', 'WITH_MANAGER', 'CHANGES', 'CONFIRMED']);

export type StepStatus = z.infer<typeof stepStatusSchema>;

export const copyStatusSchema = z.enum(['OPEN', 'WITH_MANAGER', 'ACCEPTED', 'REJECTED']);

export type CopyStatus = z.infer<typeof copyStatusSchema>;

export const practiceWaySchema = z.enum(['BY_HAND', 'WITH_TOOLS', 'WITH_AI', 'WITH_AGENT', 'NOT_YET']);

export type PracticeWay = z.infer<typeof practiceWaySchema>;

export const alignmentRoleSchema = z.enum(['EMPLOYEE', 'MANAGER']);

export type AlignmentRole = z.infer<typeof alignmentRoleSchema>;

export const positionAnswerSchema = z.enum(['CONFIRMED', 'NOT_CONFIRMED']);

export type PositionAnswer = z.infer<typeof positionAnswerSchema>;

export const alignmentPhaseSchema = z.enum(['IN_PROGRESS', 'WITH_MANAGER', 'ALIGNED']);

export type AlignmentPhase = z.infer<typeof alignmentPhaseSchema>;

export const alignmentTaskOriginSchema = z.enum(['POSITION', 'ALIGNMENT']);

export const taskCharacterSchema = z.enum(['CONTRIBUTION', 'ROUTINE']);

export type TaskCharacter = z.infer<typeof taskCharacterSchema>;

export const copyDecisionKindSchema = z.enum(['ACCEPTED', 'REJECTED']);

export const teamMemberStateSchema = z.enum(['NOT_STARTED', 'IN_PROGRESS', 'ALIGNED']);

export const copyChangeKindSchema = z.enum(['ADDED', 'REMOVED', 'CHANGED']);

export const alignmentErrorCodeSchema = z.enum([
    'STALE_REVISION',
    'NOT_ALLOWED',
    'COMMENT_REQUIRED',
    'STEP_CONFIRMED',
    'INCOMPLETE',
    'ALREADY_OPEN',
    'NOT_A_DIRECT_REPORT',
    'NOT_FOUND',
    'TASKS_GENERATING',
]);

export type AlignmentErrorCode = z.infer<typeof alignmentErrorCodeSchema>;

export const ALIGNMENT_WIRE_ERROR_CODES: Readonly<Record<AlignmentErrorCode, string>> = {
    STALE_REVISION: 'ALN0001',
    NOT_ALLOWED: 'ALN0002',
    COMMENT_REQUIRED: 'ALN0003',
    STEP_CONFIRMED: 'ALN0006',
    INCOMPLETE: 'ALN0007',
    ALREADY_OPEN: 'ALN0008',
    NOT_A_DIRECT_REPORT: 'ALN0009',
    NOT_FOUND: 'ALN0010',
    TASKS_GENERATING: 'ALN0011',
};

export const ALIGNMENT_ERROR_STATUS: Readonly<Record<AlignmentErrorCode, number>> = {
    STALE_REVISION: 409,
    NOT_ALLOWED: 409,
    COMMENT_REQUIRED: 409,
    STEP_CONFIRMED: 409,
    INCOMPLETE: 409,
    ALREADY_OPEN: 409,
    NOT_A_DIRECT_REPORT: 403,
    NOT_FOUND: 404,
    TASKS_GENERATING: 409,
};

export const alignmentErrorCodeOf = (wireCode: string): AlignmentErrorCode | undefined =>
    alignmentErrorCodeSchema.options.find((code) => ALIGNMENT_WIRE_ERROR_CODES[code] === wireCode);

export const alignmentPersonSchema = positionPersonSchema
    .pick({ email: true, first_name: true, last_name: true })
    .extend({ user_id: z.string() });

export type AlignmentPerson = z.infer<typeof alignmentPersonSchema>;

export const practiceFrequencySchema = z.enum(['DAILY', 'WEEKLY', 'MONTHLY', 'RARELY']);

export const practiceSkillSchema = z.object({
    id: z.uuid(),
    title: z.string().trim().min(1).max(256),
});

export const taskPracticeSchema = z.object({
    way: practiceWaySchema.nullish(),
    automation_share: z.number().int().min(0).max(100).nullish(),
    description: z.string().max(5000),
    matches_documentation: z.boolean().nullable().default(null),
    differences: z.string().max(5000).default(''),
    frequency: practiceFrequencySchema.nullable().default(null),
    tools: z
        .array(z.string().trim().min(1).max(80))
        .max(20)
        .refine((tools) => new Set(tools).size === tools.length)
        .default([]),
    uses_skills: z.boolean().nullable().default(null),
    skills: practiceSkillSchema
        .array()
        .max(50)
        .refine((skills) => new Set(skills.map(({ id }) => id)).size === skills.length)
        .default([]),
});

export type TaskPractice = z.infer<typeof taskPracticeSchema>;

const TASK_GENERATION_STATES = ['QUEUED', 'STARTED', 'FINISHED', 'FAILED'] as const;

/**
 * Where the AI content of a task added in the alignment stands; null for tasks that need none. The wire sends the work
 * task's lowercase state (`queued`); it is read uppercase like the alignment's other enums.
 */
export const taskGenerationStateSchema = z
    .string()
    .transform((state) => state.toUpperCase())
    .pipe(z.enum(TASK_GENERATION_STATES));

export type TaskGenerationState = z.infer<typeof taskGenerationStateSchema>;

export const alignmentTaskSchema = positionStructuredTaskSchema.extend({
    origin: alignmentTaskOriginSchema,
    removed: z.boolean().default(false),
    removed_by: alignmentRoleSchema.nullish(),
    added_by: alignmentRoleSchema.nullish(),
    character: taskCharacterSchema.nullish(),
    description: z.string().nullish(),
    practice: taskPracticeSchema.nullish(),
    generation_state: taskGenerationStateSchema.nullish(),
});

export type AlignmentTask = z.infer<typeof alignmentTaskSchema>;

export const copyStructuredSchema = positionStructuredSchema.extend({ tasks: alignmentTaskSchema.array() });

export type CopyStructured = z.infer<typeof copyStructuredSchema>;

const COPY_TITLE_MAX_LENGTH = 256;

export const copyBodySchema = z.object({
    title: z
        .string()
        .max(COPY_TITLE_MAX_LENGTH)
        .refine((title) => title.trim().length > 0),
    structured: copyStructuredSchema,
});

export type CopyBody = z.infer<typeof copyBodySchema>;

/** The position a copy was forked from, read through the alignment; `attributes` names its attribute values. */
export const alignmentPositionSchema = positionDetailSchema.extend({
    attributes: positionAttributeSchema.array().default([]),
});

export type AlignmentPosition = z.infer<typeof alignmentPositionSchema>;

export const copyRevisionSummarySchema = z.object({
    revision: z.number().int(),
    saved_at: z.string(),
    saved_by: alignmentPersonSchema.nullish(),
    role: alignmentRoleSchema,
});

export type CopyRevisionSummary = z.infer<typeof copyRevisionSummarySchema>;

export const copyBaseSchema = z.object({
    position_id: z.string(),
    architecture_id: z.string().nullish(),
    version: z.number().int(),
    forked_at: z.string(),
});

export type CopyBase = z.infer<typeof copyBaseSchema>;

export const copyDecisionSchema = z.object({
    kind: copyDecisionKindSchema,
    at: z.string(),
    by: alignmentPersonSchema.nullish(),
    reason: z.string().nullish(),
});

export type CopyDecision = z.infer<typeof copyDecisionSchema>;

export const copySavedSchema = z.object({
    version: z.number().int(),
    status: positionStatusSchema,
    at: z.string(),
});

export type CopySaved = z.infer<typeof copySavedSchema>;

export const positionCopySchema = z.object({
    id: z.string(),
    status: copyStatusSchema,
    base: copyBaseSchema,
    revision: z.number().int(),
    body: copyBodySchema,
    revisions: copyRevisionSummarySchema.array(),
    decision: copyDecisionSchema.nullish(),
    saved: copySavedSchema.nullish(),
});

export type PositionCopy = z.infer<typeof positionCopySchema>;

export const archivedCopySchema = positionCopySchema.pick({ base: true, decision: true, id: true, status: true });

export type ArchivedCopy = z.infer<typeof archivedCopySchema>;

export const stepStateSchema = z.object({
    open_comment_count: z.number().int().nonnegative().default(0),
    status: stepStatusSchema,
    person_done: z.boolean(),
    decision: managerDecisionSchema,
    decided_at: z.string().nullish(),
    viewed_at: z.string().nullish(),
    changed_after_view_at: z.string().nullish(),
});

export type StepState = z.infer<typeof stepStateSchema>;

export const alignmentStepsSchema = z.object({
    position: stepStateSchema,
    tasks: stepStateSchema,
    automations: stepStateSchema,
});

/** An automation the employee focuses on; title and short definition are a snapshot taken when it was selected. */
export const automationSelectionSchema = z.object({
    task_id: z.string(),
    automation_id: z.string(),
    title: z.string().nullish(),
    short_definition: z.string().nullish(),
    selected_at: z.string(),
});

export type AutomationSelection = z.infer<typeof automationSelectionSchema>;

export const AUTOMATION_SELECTION_LIMIT = 3;

export const automationSelectionRefSchema = automationSelectionSchema.pick({ automation_id: true, task_id: true });

export type AutomationSelectionRef = z.infer<typeof automationSelectionRefSchema>;

/**
 * The recommended automations of every task the copy keeps, in copy order, and how many may be selected. Each task
 * entry is the work task's recommendation as `/work-tasks/{taskId}/automation-recommendation` sends it.
 */
export const alignmentAutomationsSchema = z.object({
    tasks: workTaskAutomationRecommendationSchema.array(),
    limit: z.number().int().nonnegative(),
});

export type AlignmentAutomations = z.infer<typeof alignmentAutomationsSchema>;

export const capabilitiesSchema = z.object({
    comment: z.boolean().default(true),
    answer_position: z.boolean(),
    edit_tasks: z.boolean(),
    describe_tasks: z.boolean().optional(),
    add_tasks: z.boolean().default(false),
    edit_automations: z.boolean(),
    reopen: alignmentStepSchema.array(),
    decide: alignmentStepSchema.array(),
    undo_decision: alignmentStepSchema.array(),
    edit_copy: z.boolean(),
    reject: z.boolean(),
    accept: z.boolean(),
});

export type Capabilities = z.infer<typeof capabilitiesSchema>;

export const alignmentSchema = z.object({
    id: z.string(),
    revision: z.number().int(),
    viewer: alignmentRoleSchema,
    owner: alignmentPersonSchema,
    manager: alignmentPersonSchema.nullish(),
    position_answer: positionAnswerSchema.nullish(),
    steps: alignmentStepsSchema,
    automations: automationSelectionSchema.array().default([]),
    started_at: z.string(),
    phase: alignmentPhaseSchema,
    copy: positionCopySchema,
    archived_copies: archivedCopySchema.array(),
    capabilities: capabilitiesSchema,
});

export type Alignment = z.infer<typeof alignmentSchema>;

export const alignmentSummarySchema = alignmentSchema
    .pick({ id: true, manager: true, owner: true, phase: true, started_at: true, steps: true })
    .extend({
        position_title: z.string(),
        waiting_on_viewer: z.number().int(),
        aligned_at: z.string().nullish(),
        saved_version: z.number().int().nullish(),
    });

export type AlignmentSummary = z.infer<typeof alignmentSummarySchema>;

export const myAlignmentSchema = z.object({ alignment: alignmentSummarySchema.nullable() });

export type MyAlignment = z.infer<typeof myAlignmentSchema>;

export const teamMemberPositionSchema = z.object({
    id: z.string(),
    title: z.string(),
    version: z.number().int(),
});

export const teamMemberSchema = z.object({
    person: alignmentPersonSchema,
    position: teamMemberPositionSchema,
    state: teamMemberStateSchema,
    alignment: alignmentSummarySchema.nullish(),
    can_start: z.boolean(),
    previous_alignments: z
        .array(z.object({ id: z.string().uuid(), created_at: z.string().datetime({ offset: true }) }))
        .default([]),
});

export type TeamMember = z.infer<typeof teamMemberSchema>;

export const teamSchema = teamMemberSchema.array();

export const startAlignmentInputSchema = z.object({
    employee_id: z.string(),
    position_id: z.string(),
});

export type StartAlignmentInput = z.infer<typeof startAlignmentInputSchema>;

export const copyChangeSchema = z.object({
    path: z.string(),
    kind: copyChangeKindSchema,
});

export type CopyChange = z.infer<typeof copyChangeSchema>;

export const copyCompareSchema = z.object({
    left: copyBodySchema,
    right: copyBodySchema,
    changes: copyChangeSchema.array(),
});

export type CopyCompare = z.infer<typeof copyCompareSchema>;

export const compareLeftSchema = z.union([z.literal('base'), z.templateLiteral(['rev:', z.int()])]);

export type CompareLeft = z.infer<typeof compareLeftSchema>;

export const compareRightSchema = z.union([z.literal('current'), z.templateLiteral(['rev:', z.int()])]);

export type CompareRight = z.infer<typeof compareRightSchema>;

export const threadTargetSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('TITLE') }),
    z.object({ kind: z.literal('ROW'), row_id: z.string() }),
    z.object({ kind: z.literal('DESCRIPTION_SECTION'), section_id: z.string() }),
]);

export type ThreadTarget = z.infer<typeof threadTargetSchema>;

export const threadAnchorInputSchema = z.object({
    exact: z.string().min(1).max(10000),
    prefix: z.string().nullish(),
    suffix: z.string().nullish(),
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative(),
});

export type ThreadAnchor = z.infer<typeof threadAnchorInputSchema>;

export const threadResolutionSchema = z.object({
    resolved_at: z.string(),
    resolved_by: alignmentPersonSchema.nullish(),
});

export const alignmentThreadSchema = z.object({
    id: z.string(),
    parent_id: z.string().nullish(),
    step: alignmentStepSchema,
    copy_id: z.string(),
    copy_revision: z.number().int(),
    target: threadTargetSchema,
    anchor: threadAnchorInputSchema.nullish(),
    content: z.string(),
    author: alignmentPersonSchema.nullish(),
    created_at: z.string(),
    resolution: threadResolutionSchema.nullish(),
    read_only: z.boolean(),
});

export type AlignmentThread = z.infer<typeof alignmentThreadSchema>;

export const alignmentThreadsSchema = alignmentThreadSchema.array();

export const threadInputSchema = z.object({
    parent_id: z.string().nullish(),
    step: alignmentStepSchema,
    target: threadTargetSchema,
    anchor: threadAnchorInputSchema.nullish(),
    content: z.string().min(1).max(5000),
});

export type ThreadInput = z.infer<typeof threadInputSchema>;

export const revisionedRequestSchema = z.object({ expected_revision: z.number().int() });

export const positionAnswerRequestSchema = revisionedRequestSchema.extend({
    answer: positionAnswerSchema,
    reason: z.string().max(5000).nullish(),
});

export const taskPracticeRequestSchema = revisionedRequestSchema.extend(taskPracticeSchema.shape);

export const addTaskInputSchema = z.object({
    title: z.string().min(1).max(256),
    character: taskCharacterSchema.nullish(),
});

/**
 * A task of the copy as its tile reads it: the task-management task detail, and whether the caller may edit it.
 * The wire wraps the detail as `{ task, editable }`; it is read flat.
 */
export const alignmentTaskDetailSchema = z
    .object({ task: taskManagementTaskDetailSchema, editable: z.boolean().default(false) })
    .transform(({ editable, task }) => ({ ...task, editable }));

export type AlignmentTaskDetail = z.infer<typeof alignmentTaskDetailSchema>;

const taskEditWorkflowStepSchema = z.object({
    title: z.string().trim().min(1).max(256),
    description: z.string().trim().min(1).max(100_000),
});

/** `workflow` replaces the task's steps when sent; it is left out to keep them, and cannot clear them. */
export const taskEditInputSchema = z.object({
    title: z.string().min(1).max(256),
    description: z.string().max(100_000),
    workflow: z.object({ steps: taskEditWorkflowStepSchema.array().min(1) }).optional(),
});

export type TaskEditInput = z.infer<typeof taskEditInputSchema>;

export const taskEditRequestSchema = revisionedRequestSchema.extend(taskEditInputSchema.shape);

export type AddTaskInput = z.infer<typeof addTaskInputSchema>;

export const addTaskRequestSchema = revisionedRequestSchema.extend(addTaskInputSchema.shape);

export const taskRemovedRequestSchema = revisionedRequestSchema.extend({ removed: z.boolean() });

export const automationSelectionRequestSchema = revisionedRequestSchema.extend({
    selections: automationSelectionRefSchema.array().max(AUTOMATION_SELECTION_LIMIT),
});

export const rejectCopyRequestSchema = revisionedRequestSchema.extend({ reason: z.string().min(1).max(2000) });

export const decisionRequestSchema = revisionedRequestSchema.extend({
    decision: managerDecisionSchema.exclude(['PENDING']),
    reason: z.string().max(5000).nullish(),
});

export const saveCopyRequestSchema = revisionedRequestSchema.extend({ body: copyBodySchema });
